import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Search,
  Star,
  Users,
  FileText,
  Eye,
  Heart,
  MessageCircle,
  Filter,
  Award,
  TrendingUp,
  BookOpen,
  Video,
  ExternalLink,
  Send,
  ArrowLeft,
  Calendar,
  CheckCircle,
  Clock,
  User,
  Sparkles
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Expertise options
const EXPERTISE_OPTIONS = [
  { value: 'stocks', label_en: 'DSE Stocks', label_sw: 'Hisa za DSE' },
  { value: 'bonds', label_en: 'Government Bonds', label_sw: 'Hatifungani za Serikali' },
  { value: 'treasury_bills', label_en: 'Treasury Bills', label_sw: 'Dhamana za Hazina' },
  { value: 'utt', label_en: 'UTT AMIS Funds', label_sw: 'Mifuko ya UTT AMIS' },
  { value: 'personal_finance', label_en: 'Personal Finance', label_sw: 'Fedha Binafsi' },
  { value: 'risk_management', label_en: 'Risk Management', label_sw: 'Usimamizi wa Hatari' },
  { value: 'etfs', label_en: 'ETFs', label_sw: 'ETF' }
];

// Star Rating Component
const StarRating = ({ rating, size = 'sm' }) => {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  const iconSize = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          className={`${iconSize} ${
            i < fullStars
              ? 'fill-yellow-400 text-yellow-400'
              : i === fullStars && hasHalfStar
              ? 'fill-yellow-400/50 text-yellow-400'
              : 'text-gray-300'
          }`}
        />
      ))}
      <span className="ml-1 text-sm font-medium">{rating.toFixed(1)}</span>
    </div>
  );
};

// Educator Card Component
const EducatorCard = ({ educator, t, language, onContact, onViewProfile }) => {
  const getExpertiseLabel = (value) => {
    const exp = EXPERTISE_OPTIONS.find(e => e.value === value);
    return exp ? (language === 'sw' ? exp.label_sw : exp.label_en) : value;
  };

  return (
    <Card className="border-border/50 hover:shadow-lg transition-all duration-300 group">
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          {/* Avatar */}
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center flex-shrink-0">
            <span className="text-2xl font-bold text-primary">
              {educator.full_name?.charAt(0) || 'E'}
            </span>
          </div>
          
          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-bold text-lg text-foreground group-hover:text-primary transition-colors">
                  {educator.full_name}
                </h3>
                <StarRating rating={educator.rating || 3} />
              </div>
              <Button 
                size="sm" 
                variant="ghost"
                onClick={() => onViewProfile(educator)}
                className="text-primary"
              >
                {t('View', 'Tazama')}
              </Button>
            </div>
            
            {/* Bio */}
            <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
              {educator.bio || t('Financial educator on Wekeza-Smart', 'Mwalimu wa fedha kwenye Wekeza-Smart')}
            </p>
            
            {/* Expertise */}
            <div className="flex flex-wrap gap-1.5 mt-3">
              {educator.expertise?.slice(0, 3).map((exp, i) => (
                <Badge key={i} variant="secondary" className="text-xs">
                  {getExpertiseLabel(exp)}
                </Badge>
              ))}
              {educator.expertise?.length > 3 && (
                <Badge variant="outline" className="text-xs">
                  +{educator.expertise.length - 3}
                </Badge>
              )}
            </div>
            
            {/* Stats */}
            <div className="flex items-center gap-4 mt-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <FileText className="w-4 h-4" />
                <span>{educator.content_count || 0}</span>
              </div>
              <div className="flex items-center gap-1">
                <Eye className="w-4 h-4" />
                <span>{(educator.total_views || 0).toLocaleString()}</span>
              </div>
              <div className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                <span>{educator.referrals_count || 0}</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Action Button */}
        <Button 
          className="w-full mt-4 rounded-full"
          onClick={() => onContact(educator)}
          data-testid={`contact-educator-${educator.id}`}
        >
          <MessageCircle className="w-4 h-4 mr-2" />
          {t('Contact Educator', 'Wasiliana na Mwalimu')}
        </Button>
      </CardContent>
    </Card>
  );
};

// Educator Profile Modal
const EducatorProfileModal = ({ educator, isOpen, onClose, t, language, onContact }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen && educator) {
      fetchProfile();
    }
  }, [isOpen, educator]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/educators/${educator.id}/public`);
      setProfile(res.data);
    } catch (error) {
      toast.error(t('Failed to load profile', 'Imeshindwa kupakia wasifu'));
    } finally {
      setLoading(false);
    }
  };

  const getExpertiseLabel = (value) => {
    const exp = EXPERTISE_OPTIONS.find(e => e.value === value);
    return exp ? (language === 'sw' ? exp.label_sw : exp.label_en) : value;
  };

  const getContentIcon = (type) => {
    switch(type) {
      case 'video': return <Video className="w-5 h-5 text-red-500" />;
      case 'article': return <FileText className="w-5 h-5 text-blue-500" />;
      case 'course': return <BookOpen className="w-5 h-5 text-purple-500" />;
      default: return <FileText className="w-5 h-5" />;
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : profile ? (
          <>
            <DialogHeader>
              <div className="flex items-start gap-4">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                  <span className="text-3xl font-bold text-primary">
                    {profile.educator.full_name?.charAt(0)}
                  </span>
                </div>
                <div className="flex-1">
                  <DialogTitle className="text-2xl">{profile.educator.full_name}</DialogTitle>
                  <StarRating rating={profile.educator.rating || 3} size="md" />
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('Joined', 'Alijiunga')} {new Date(profile.educator.joined_date).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </DialogHeader>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-4 my-6">
              <div className="text-center p-3 rounded-lg bg-muted/50">
                <p className="text-2xl font-bold text-primary">{profile.educator.stats.content_count}</p>
                <p className="text-xs text-muted-foreground">{t('Content', 'Maudhui')}</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-muted/50">
                <p className="text-2xl font-bold text-blue-600">{profile.educator.stats.total_views.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">{t('Views', 'Maoni')}</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-muted/50">
                <p className="text-2xl font-bold text-red-500">{profile.educator.stats.total_likes}</p>
                <p className="text-xs text-muted-foreground">{t('Likes', 'Kupenda')}</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-muted/50">
                <p className="text-2xl font-bold text-emerald-600">{profile.educator.stats.referrals_count}</p>
                <p className="text-xs text-muted-foreground">{t('Students', 'Wanafunzi')}</p>
              </div>
            </div>

            {/* Bio */}
            <div className="mb-6">
              <h4 className="font-semibold mb-2">{t('About', 'Kuhusu')}</h4>
              <p className="text-muted-foreground">
                {profile.educator.bio || t('No bio provided', 'Hakuna maelezo')}
              </p>
            </div>

            {/* Expertise */}
            <div className="mb-6">
              <h4 className="font-semibold mb-2">{t('Expertise', 'Utaalamu')}</h4>
              <div className="flex flex-wrap gap-2">
                {profile.educator.expertise?.map((exp, i) => (
                  <Badge key={i} variant="secondary">
                    {getExpertiseLabel(exp)}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Content */}
            {profile.content?.length > 0 && (
              <div className="mb-6">
                <h4 className="font-semibold mb-3">{t('Educational Content', 'Maudhui ya Elimu')}</h4>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {profile.content.map((item) => (
                    <div 
                      key={item.id} 
                      className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/learn`)}
                    >
                      {getContentIcon(item.content_type)}
                      <div className="flex-1">
                        <p className="font-medium text-sm">{item.title}</p>
                        <p className="text-xs text-muted-foreground line-clamp-1">{item.description}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" /> {item.views}
                          </span>
                          <span className="flex items-center gap-1">
                            <Heart className="w-3 h-3" /> {item.likes}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Contact Button */}
            <Button 
              className="w-full rounded-full" 
              size="lg"
              onClick={() => {
                onClose();
                onContact(profile.educator);
              }}
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              {t('Send Message', 'Tuma Ujumbe')}
            </Button>
          </>
        ) : (
          <div className="text-center py-8">
            <p className="text-muted-foreground">{t('Profile not found', 'Wasifu haupatikani')}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Contact Modal
const ContactEducatorModal = ({ educator, isOpen, onClose, t, token }) => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.error(t('Please fill in all fields', 'Tafadhali jaza sehemu zote'));
      return;
    }

    try {
      setSending(true);
      await axios.post(`${API}/messages/send`, {
        educator_id: educator.id,
        subject,
        message
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(t('Message sent successfully!', 'Ujumbe umetumwa!'));
      setSubject('');
      setMessage('');
      onClose();
    } catch (error) {
      toast.error(t('Failed to send message', 'Imeshindwa kutuma ujumbe'));
    } finally {
      setSending(false);
    }
  };

  if (!isOpen || !educator) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-primary" />
            {t('Contact', 'Wasiliana na')} {educator.full_name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>{t('Subject', 'Mada')}</Label>
            <Input
              placeholder={t('e.g. Question about DSE stocks', 'mfano: Swali kuhusu hisa za DSE')}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="message-subject"
            />
          </div>

          <div className="space-y-2">
            <Label>{t('Message', 'Ujumbe')}</Label>
            <Textarea
              placeholder={t('Write your message here...', 'Andika ujumbe wako hapa...')}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              data-testid="message-body"
            />
          </div>

          <div className="bg-muted/50 p-3 rounded-lg text-sm text-muted-foreground">
            <p className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 mt-0.5 text-primary" />
              {t(
                'Your message will be sent through Wekeza-Smart. The educator will reply in the app.',
                'Ujumbe wako utatumwa kupitia Wekeza-Smart. Mwalimu atajibu ndani ya programu.'
              )}
            </p>
          </div>

          <Button 
            className="w-full rounded-full" 
            onClick={handleSend}
            disabled={sending}
            data-testid="send-message-btn"
          >
            {sending ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            {t('Send Message', 'Tuma Ujumbe')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main Component
export default function FindEducator() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { token } = useAuth();
  const [educators, setEducators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expertiseFilter, setExpertiseFilter] = useState('');
  const [sortBy, setSortBy] = useState('rating');
  const [selectedEducator, setSelectedEducator] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [showContact, setShowContact] = useState(false);

  const fetchEducators = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (expertiseFilter) params.append('expertise', expertiseFilter);
      params.append('sort_by', sortBy);
      
      const res = await axios.get(`${API}/educators?${params.toString()}`);
      setEducators(res.data.educators || []);
    } catch (error) {
      toast.error(t('Failed to load educators', 'Imeshindwa kupakia walimu'));
    } finally {
      setLoading(false);
    }
  }, [expertiseFilter, sortBy, t]);

  useEffect(() => {
    fetchEducators();
  }, [fetchEducators]);

  const filteredEducators = educators.filter(edu => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      edu.full_name?.toLowerCase().includes(query) ||
      edu.bio?.toLowerCase().includes(query) ||
      edu.expertise?.some(e => e.toLowerCase().includes(query))
    );
  });

  const handleViewProfile = (educator) => {
    setSelectedEducator(educator);
    setShowProfile(true);
  };

  const handleContact = (educator) => {
    setSelectedEducator(educator);
    setShowContact(true);
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 rounded-xl bg-primary/10">
              <Sparkles className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground">
                {t('Find a Financial Educator', 'Tafuta Mwalimu wa Fedha')}
              </h1>
              <p className="text-muted-foreground">
                {t('Get personalized investment guidance from certified experts', 'Pata mwongozo wa uwekezaji kutoka kwa wataalamu walioidhinishwa')}
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6 border-border/50">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  placeholder={t('Search by name or expertise...', 'Tafuta kwa jina au utaalamu...')}
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="educator-search"
                />
              </div>

              {/* Expertise Filter */}
              <Select value={expertiseFilter || "all"} onValueChange={(v) => setExpertiseFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="w-full md:w-48" data-testid="expertise-filter">
                  <SelectValue placeholder={t('All Expertise', 'Utaalamu Wote')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('All Expertise', 'Utaalamu Wote')}</SelectItem>
                  {EXPERTISE_OPTIONS.map(exp => (
                    <SelectItem key={exp.value} value={exp.value}>
                      {language === 'sw' ? exp.label_sw : exp.label_en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Sort */}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full md:w-40" data-testid="sort-filter">
                  <SelectValue placeholder={t('Sort by', 'Panga kwa')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rating">{t('Top Rated', 'Alama Bora')}</SelectItem>
                  <SelectItem value="popular">{t('Most Popular', 'Maarufu Zaidi')}</SelectItem>
                  <SelectItem value="content">{t('Most Content', 'Maudhui Mengi')}</SelectItem>
                  <SelectItem value="newest">{t('Newest', 'Mpya Zaidi')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredEducators.length > 0 ? (
          <>
            <p className="text-sm text-muted-foreground mb-4">
              {t('Showing', 'Inaonyesha')} {filteredEducators.length} {t('educators', 'walimu')}
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredEducators.map((educator) => (
                <EducatorCard
                  key={educator.id}
                  educator={educator}
                  t={t}
                  language={language}
                  onContact={handleContact}
                  onViewProfile={handleViewProfile}
                />
              ))}
            </div>
          </>
        ) : (
          <Card className="border-border/50">
            <CardContent className="py-12 text-center">
              <Users className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-semibold text-foreground mb-2">
                {t('No educators found', 'Hakuna walimu waliopatikana')}
              </h3>
              <p className="text-muted-foreground mb-4">
                {searchQuery || expertiseFilter
                  ? t('Try adjusting your filters', 'Jaribu kubadilisha vichujio vyako')
                  : t('Be the first to join as an educator!', 'Kuwa wa kwanza kujiunga kama mwalimu!')}
              </p>
              {(searchQuery || expertiseFilter) && (
                <Button variant="outline" onClick={() => { setSearchQuery(''); setExpertiseFilter(''); }}>
                  {t('Clear Filters', 'Futa Vichujio')}
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Become Educator CTA */}
        <Card className="mt-8 bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
          <CardContent className="py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-primary/20 flex items-center justify-center">
                  <Award className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-foreground">
                    {t('Are you a financial expert?', 'Je, wewe ni mtaalamu wa fedha?')}
                  </h3>
                  <p className="text-muted-foreground">
                    {t('Join our educator program and earn 15% commission', 'Jiunge na programu yetu ya walimu na upate kamisheni ya 15%')}
                  </p>
                </div>
              </div>
              <Button onClick={() => navigate('/educators')} className="rounded-full">
                {t('Become an Educator', 'Kuwa Mwalimu')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Modals */}
      <EducatorProfileModal
        educator={selectedEducator}
        isOpen={showProfile}
        onClose={() => setShowProfile(false)}
        t={t}
        language={language}
        onContact={handleContact}
      />

      <ContactEducatorModal
        educator={selectedEducator}
        isOpen={showContact}
        onClose={() => setShowContact(false)}
        t={t}
        token={token}
      />
    </Layout>
  );
}
